import React, {useCallback, useEffect, useState} from 'react';
import PathSvgHandles from './handles';
import {Point} from '../../../../data/models/LessonContent/ContentFreeform';
import {MIN_SHAPE_POINTS} from './utils';
import {BASELINE_SVG_CANVAS_WIDTH} from '../SvgCanvas';

interface Props {
  shapePoints: Point[];
  updateShapePoints?: (points: Point[]) => void;
  onClick?: () => void;
  hideShape?: boolean;
  width: number;
}

const EditorSvgPath = React.memo(
  ({shapePoints, updateShapePoints, onClick, hideShape, width}: Props) => {
    const [points, setPoints] = useState<{[key: number]: Point}>([]);
    const [position, setPosition] = React.useState<any>({
      active: false,
      offset: {x: 0, y: 0},
      initial: [],
    });

    useEffect(() => {
      if (shapePoints.length && !position.active) {
        const pointsObj = {};
        Object.assign(pointsObj, shapePoints);
        setPoints(pointsObj);
      }
    }, [shapePoints]);

    const onPathClick = (e: any) => {
      if (onClick) {
        onClick();
        e.stopPropagation();
      }
    };

    const getScaleFactor = () => {
      const scale = (width * 100) / BASELINE_SVG_CANVAS_WIDTH;
      return scale / 100;
    };

    const handlePointerDown = (e: any) => {
      if (!updateShapePoints) return;
      const el = e.target;
      const x = e.clientX;
      const y = e.clientY;
      el.setPointerCapture(e.pointerId);
      setPosition({
        ...position,
        active: true,
        offset: {
          x,
          y,
        },
        initial: {...points},
      });
    };

    const handlePointerMove = (e: any) => {
      const x = e.clientX;
      const y = e.clientY;
      if (position.active) {
        let newPoints = {...points};
        const handles = Object.keys(points);
        handles.forEach((handle: string) => {
          newPoints = {
            ...newPoints,
            [parseInt(handle, 10)]: {
              x:
                position.initial[parseInt(handle, 10)].x -
                (position.offset.x - x) / getScaleFactor(),
              y:
                position.initial[parseInt(handle, 10)].y -
                (position.offset.y - y) / getScaleFactor(),
            },
          };
        });
        setPoints(newPoints);
      }
    };

    const handlePointerUp = () => {
      setPosition({
        ...position,
        active: false,
      });
      notifyParentAboutPointsChange();
    };

    const notifyParentAboutPointsChange = () => {
      if (!updateShapePoints) return;
      if (Object.keys(points).length >= MIN_SHAPE_POINTS) {
        updateShapePoints(
          Object.keys(points)
            .sort((x, y) => (x < y ? 1 : -1))
            .map(key => points[parseInt(key, 10)]),
        );
      }
    };

    const getObjectPath = useCallback(() => {
      let path = 'M';
      if (Object.keys(points).length === 0) {
        return '';
      }

      Object.keys(points)
        .sort((x, y) => (x < y ? 1 : -1))
        .forEach((handle: string) => {
          if (points[parseInt(handle, 10)]) {
            path +=
              points[parseInt(handle, 10)].x +
              ' ' +
              points[parseInt(handle, 10)].y +
              ' ';
          }
        });
      path += 'z';
      return path;
    }, [points]);

    return (
      <g>
        <path
          fillOpacity={0.4}
          fill={hideShape ? 'transparent' : '#3DA2FF'}
          d={getObjectPath()}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerMove={handlePointerMove}
          onClick={onPathClick}
          cursor={updateShapePoints ? 'move' : 'pointer'}
        />
        {updateShapePoints && (
          <PathSvgHandles
            points={points}
            setPoints={setPoints}
            onDragFinished={notifyParentAboutPointsChange}
            width={width}
          />
        )}
      </g>
    );
  },
);

export default EditorSvgPath;
